// SectionToolbar.jsx — Section identity + section-specific actions

import React, { useCallback } from 'react';

import { useShellChrome } from 'context/ShellChromeContext';
import { useTheme } from 'context/ThemeContext';

import TypeIcon from 'components/typeicon/TypeIcon';
import Icon from 'svg/Icon';

import './SectionToolbar.css';

/**
 * SectionToolbar
 *
 * Desktop chrome below GlobalToolbar. Left: section icon + title from
 * ShellChromeContext (or a custom identity portal). Right: action controls
 * via ShellSectionActions portal.
 *
 * @returns {ReactNode}
 */
const SectionToolbar = React.memo(() => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const {
    identity,
    hasCustomIdentity,
    setActionsSlotEl,
    setIdentitySlotEl,
  } = useShellChrome();

  // ----- HANDLERS ----- //

  /**
   * Publishes the identity slot element into ShellChromeContext.
   *
   * @param {HTMLElement|null} node
   */
  const handleIdentitySlotRef = useCallback((node) => {
    setIdentitySlotEl(node);
  }, [setIdentitySlotEl]);

  /**
   * Publishes the actions slot element into ShellChromeContext.
   *
   * @param {HTMLElement|null} node
   */
  const handleActionsSlotRef = useCallback((node) => {
    setActionsSlotEl(node);
  }, [setActionsSlotEl]);

  // ----- RENDER ----- //

  const chromeTitle = String(identity?.title || '').trim();
  const chromeTypeKey = String(identity?.typeKey || '').trim();
  const chromeIconKey = String(identity?.iconKey || '').trim();
  const chromeIconResource = String(identity?.iconResource || '').trim();
  const hasIdentity = Boolean(
    chromeTitle || chromeTypeKey || chromeIconKey || chromeIconResource,
  );

  if (!hasIdentity && !hasCustomIdentity) return null;

  return (
    <div
      className="section-toolbar-container"
      role="toolbar"
      aria-label="Section"
    >

      {/* IDENTITY SLOT — default chrome, or ShellSectionIdentity portal */}
      <div
        ref={handleIdentitySlotRef}
        className="section-toolbar-identity"
      >
        {!hasCustomIdentity ? (
          <>
            {chromeIconResource ? (
              <img
                alt=""
                className="section-toolbar-identity-resource"
                draggable={false}
                src={chromeIconResource}
              />
            ) : chromeIconKey ? (
              <div className="section-toolbar-identity-icon">
                <Icon
                  iconKey={chromeIconKey}
                  color={theme.foreground}
                  width={28}
                  height={28}
                />
              </div>
            ) : chromeTypeKey ? (
              <div className="section-toolbar-identity-icon">
                <TypeIcon typeKey={chromeTypeKey} size={28} />
              </div>
            ) : null}
            {chromeTitle ? (
              <div
                className="section-toolbar-identity-title"
                style={{ color: theme.foreground }}
                title={chromeTitle}
              >
                {chromeTitle}
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {/* ACTIONS SLOT — filled by ShellSectionActions portals */}
      <div
        ref={handleActionsSlotRef}
        className="section-toolbar-actions"
      />
    </div>
  );
});

SectionToolbar.displayName = 'SectionToolbar';

export default SectionToolbar;
